import { Vec2, TrackPoint, ROAD_WIDTH, POINTS_PER_SEGMENT, KART_RADIUS, RAMP_HEIGHT, RAMP_LENGTH, RAMP_WIDTH_MULT, CoinState } from './types';
import { config } from './config';

// ============================================================
// Catmull-Rom Spline
// ============================================================

function catmullRom(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, t: number): Vec2 {
  const t2 = t * t;
  const t3 = t2 * t;
  return {
    x:
      0.5 *
      (2 * p1.x +
        (-p0.x + p2.x) * t +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    z:
      0.5 *
      (2 * p1.z +
        (-p0.z + p2.z) * t +
        (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 +
        (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
  };
}

// ============================================================
// Track Path Expansion
// ============================================================

/** Expand control points into a dense smooth closed path */
export function expandTrackPath(
  controlPoints: Vec2[],
  pointsPerSegment: number = POINTS_PER_SEGMENT
): Vec2[] {
  const n = controlPoints.length;
  const path: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = controlPoints[(i - 1 + n) % n];
    const p1 = controlPoints[i];
    const p2 = controlPoints[(i + 1) % n];
    const p3 = controlPoints[(i + 2) % n];
    for (let j = 0; j < pointsPerSegment; j++) {
      const t = j / pointsPerSegment;
      path.push(catmullRom(p0, p1, p2, p3, t));
    }
  }
  return path;
}

/** Compute direction (tangent) and normal for each expanded point */
export function computeTrackNormals(path: Vec2[]): TrackPoint[] {
  const n = path.length;
  const result: TrackPoint[] = [];
  for (let i = 0; i < n; i++) {
    const prev = path[(i - 1 + n) % n];
    const next = path[(i + 1) % n];
    let dx = next.x - prev.x;
    let dz = next.z - prev.z;
    const len = Math.sqrt(dx * dx + dz * dz) || 1;
    dx /= len;
    dz /= len;
    // Normal points to the right of the travel direction
    const nx = -dz;
    const nz = dx;
    result.push({ x: path[i].x, z: path[i].z, nx, nz, dx, dz });
  }
  return result;
}

// ============================================================
// Track Queries
// ============================================================

export interface ClosestResult {
  index: number;
  distance: number;
  lateralOffset: number; // positive = right of center
  progress: number; // 0..1 along the track
}

/** Find the closest expanded track point to a world position */
export function findClosestPoint(points: TrackPoint[], x: number, z: number): ClosestResult {
  let minDist = Infinity;
  let minIdx = 0;
  for (let i = 0; i < points.length; i++) {
    const dx = x - points[i].x;
    const dz = z - points[i].z;
    const d2 = dx * dx + dz * dz;
    if (d2 < minDist) {
      minDist = d2;
      minIdx = i;
    }
  }
  const p = points[minIdx];
  const dx = x - p.x;
  const dz = z - p.z;
  const lateralOffset = dx * p.nx + dz * p.nz;
  return {
    index: minIdx,
    distance: Math.sqrt(minDist),
    lateralOffset,
    progress: minIdx / points.length,
  };
}

export interface WallCheck {
  hit: boolean;
  pushX: number;
  pushZ: number;
  index: number;
  normalX: number;
  normalZ: number;
}

/** Check if a position is outside the track boundaries and compute push-back */
export function checkWallCollision(
  points: TrackPoint[],
  x: number,
  z: number,
  halfWidth: number = ROAD_WIDTH / 2,
  kartRadius: number = KART_RADIUS
): WallCheck {
  const closest = findClosestPoint(points, x, z);
  const p = points[closest.index];
  const limit = halfWidth - kartRadius;

  if (Math.abs(closest.lateralOffset) > limit) {
    const sign = closest.lateralOffset > 0 ? 1 : -1;
    return {
      hit: true,
      pushX: p.x + p.nx * sign * limit,
      pushZ: p.z + p.nz * sign * limit,
      index: closest.index,
      normalX: -p.nx * sign,
      normalZ: -p.nz * sign,
    };
  }

  return { hit: false, pushX: x, pushZ: z, index: closest.index, normalX: 0, normalZ: 0 };
}

// ============================================================
// Ramp Data & Ground Check
// ============================================================

export interface RampData {
  x: number;         // ramp base position (ground level)
  z: number;
  forwardX: number;  // slope direction (track tangent, normalized)
  forwardZ: number;
  width: number;
  length: number;
  height: number;
  slopeFactor: number; // height / length (for vy calculation)
}

export interface RampCheck {
  height: number;      // ground height at this position
  onRamp: boolean;
  forwardX: number;    // ramp forward direction (only valid if onRamp)
  forwardZ: number;
  slopeFactor: number; // height / length (only valid if onRamp)
}

/** Build ramp collision data from track path and ramp positions */
export function buildRampData(
  path: TrackPoint[],
  rampPositions: number[],
  roadWidth: number
): RampData[] {
  const n = path.length;
  const ramps: RampData[] = [];
  const lateralOffsets = [0, -roadWidth * 0.1, roadWidth * 0.1];
  const rampWidth = roadWidth * RAMP_WIDTH_MULT;

  for (let ri = 0; ri < rampPositions.length; ri++) {
    const pathIdx = Math.floor(rampPositions[ri] * n) % n;
    const p = path[pathIdx];
    const lateralOffset = lateralOffsets[ri % lateralOffsets.length];

    ramps.push({
      x: p.x + p.nx * lateralOffset,
      z: p.z + p.nz * lateralOffset,
      forwardX: p.dx,
      forwardZ: p.dz,
      width: rampWidth,
      length: RAMP_LENGTH,
      height: RAMP_HEIGHT,
      slopeFactor: RAMP_HEIGHT / RAMP_LENGTH,
    });
  }

  return ramps;
}

/** Check ground height at a kart's (x, z) position against all ramps */
export function checkRampGround(ramps: RampData[], kartX: number, kartZ: number): RampCheck {
  for (const ramp of ramps) {
    const relX = kartX - ramp.x;
    const relZ = kartZ - ramp.z;

    // Project onto ramp-local axes
    const along = relX * ramp.forwardX + relZ * ramp.forwardZ;
    const across = relX * (-ramp.forwardZ) + relZ * ramp.forwardX;

    if (along >= 0 && along <= ramp.length && Math.abs(across) <= ramp.width / 2) {
      const fraction = along / ramp.length;
      return {
        height: fraction * ramp.height,
        onRamp: true,
        forwardX: ramp.forwardX,
        forwardZ: ramp.forwardZ,
        slopeFactor: ramp.slopeFactor,
      };
    }
  }

  return { height: 0, onRamp: false, forwardX: 0, forwardZ: 0, slopeFactor: 0 };
}

// ============================================================
// Checkpoint / Lap Logic
// ============================================================

const NUM_SECTORS = 4;

/** Get the sector (0..NUM_SECTORS-1) for a track index */
export function getSector(trackIndex: number, totalPoints: number): number {
  return Math.floor((NUM_SECTORS * trackIndex) / totalPoints);
}

/**
 * Update checkpoint & lap state for a kart.
 * Returns { checkpoint, lap, bits, lapCompleted }.
 */
export function updateCheckpoint(
  trackIndex: number,
  totalPoints: number,
  currentCheckpoint: number,
  currentLap: number,
  checkpointBits: number // bitmask of visited sectors
): {
  checkpoint: number;
  lap: number;
  bits: number;
  lapCompleted: boolean;
} {
  const sector = getSector(trackIndex, totalPoints);
  let checkpoint = currentCheckpoint;
  let lap = currentLap;
  let bits = checkpointBits;
  let lapCompleted = false;

  if (sector !== currentCheckpoint) {
    const expected = (currentCheckpoint + 1) % NUM_SECTORS;
    if (sector === expected) {
      if (sector === 0 && bits === 0b111) {
        lap++;
        bits = 0;
        lapCompleted = true;
      } else if (sector > 0) {
        bits |= 1 << (sector - 1);
      }
      checkpoint = sector;
    }
  }

  return { checkpoint, lap, bits, lapCompleted };
}

// ============================================================
// Default Track Definition (Turbo Circuit — for multiplayer)
// ============================================================

/** Control points for "Turbo Circuit" — a rounded rectangle */
export const TRACK_CONTROL_POINTS: Vec2[] = [
  { x: 0, z: -70 },
  { x: 30, z: -70 },
  { x: 60, z: -55 },
  { x: 70, z: -25 },
  { x: 70, z: 25 },
  { x: 60, z: 55 },
  { x: 30, z: 70 },
  { x: -30, z: 70 },
  { x: -60, z: 55 },
  { x: -70, z: 25 },
  { x: -70, z: -25 },
  { x: -60, z: -55 },
  { x: -30, z: -70 },
];

/** Default start grid (8 positions for multiplayer, 12 for solo are defined in maps.ts) */
export const START_POSITIONS: Array<{ x: number; z: number; yaw: number }> = [
  { x: -1, z: -68, yaw: 0 },
  { x: -1, z: -72, yaw: 0 },
  { x: -5, z: -68, yaw: 0 },
  { x: -5, z: -72, yaw: 0 },
  { x: -9, z: -68, yaw: 0 },
  { x: -9, z: -72, yaw: 0 },
  { x: -13, z: -68, yaw: 0 },
  { x: -13, z: -72, yaw: 0 },
  { x: -17, z: -68, yaw: 0 },
  { x: -17, z: -72, yaw: 0 },
  { x: -21, z: -68, yaw: 0 },
  { x: -21, z: -72, yaw: 0 },
];

// ============================================================
// Track Initialization (parameterized for any map)
// ============================================================

export interface InitializedTrack {
  controlPoints: Vec2[];
  path: TrackPoint[];
  numPoints: number;
  itemPositions: Vec2[];
  coinPositions: Vec2[];
  startPositions: Array<{ x: number; z: number; yaw: number }>;
  roadWidth: number;
  ramps: RampData[];
}

/** Expand control points, compute normals, place item boxes */
export function initTrack(
  controlPoints: Vec2[] = TRACK_CONTROL_POINTS,
  startPositions: Array<{ x: number; z: number; yaw: number }> = START_POSITIONS,
  roadWidth: number = ROAD_WIDTH,
  rampPositions: number[] = []
): InitializedTrack {
  const raw = expandTrackPath(controlPoints);
  const path = computeTrackNormals(raw);
  const numPoints = path.length;

  // Place item boxes at 4 evenly spaced groups, 3 boxes per group (left/center/right)
  const itemPositions: Vec2[] = [];
  const spread = Math.min(roadWidth / 2 - 1, 4);
  for (let g = 0; g < 4; g++) {
    const idx = (Math.floor(numPoints * 0.1) + g * Math.floor(numPoints / 4)) % numPoints;
    const p = path[idx];
    itemPositions.push({ x: p.x - p.nx * spread, z: p.z - p.nz * spread });
    itemPositions.push({ x: p.x, z: p.z });
    itemPositions.push({ x: p.x + p.nx * spread, z: p.z + p.nz * spread });
  }

  // Build ramp collision data
  const ramps = buildRampData(path, rampPositions, roadWidth);

  // Generate coin positions along the track
  const coinPositions = generateCoinPositions(path, numPoints, roadWidth);

  return {
    controlPoints,
    path,
    numPoints,
    itemPositions,
    coinPositions,
    startPositions,
    roadWidth,
    ramps,
  };
}

// ============================================================
// Coin Placement
// ============================================================

/** Generate coin world positions evenly spaced along the track */
function generateCoinPositions(
  path: TrackPoint[],
  numPoints: number,
  roadWidth: number
): Vec2[] {
  const coins: Vec2[] = [];
  const coinsPerTrack = config.coins.coinsPerTrack;
  const coinsAcross = config.coins.coinsAcross;
  const halfWidth = roadWidth / 2;
  const coinSpread = halfWidth * 0.5; // coins at 0, ±50% of half-width

  for (let g = 0; g < coinsPerTrack; g++) {
    const idx = Math.floor((g / coinsPerTrack) * numPoints) % numPoints;
    const p = path[idx];

    for (let c = 0; c < coinsAcross; c++) {
      const offset = coinsAcross === 1 ? 0 :
        ((c / (coinsAcross - 1)) - 0.5) * 2 * coinSpread;
      coins.push({
        x: p.x + p.nx * offset,
        z: p.z + p.nz * offset,
      });
    }
  }

  return coins;
}

// ============================================================
// Off-Road Detection
// ============================================================

/** Check if position is off the road surface */
export function isOffRoad(
  path: TrackPoint[],
  x: number,
  z: number,
  halfWidth: number,
  threshold: number = config.offroad.lateralThreshold
): boolean {
  const closest = findClosestPoint(path, x, z);
  const ratio = Math.abs(closest.lateralOffset) / halfWidth;
  return ratio > threshold;
}

// ============================================================
// Wrong-Way Detection
// ============================================================

/** Check if a kart is facing the wrong direction on the track */
export function isWrongWay(
  path: TrackPoint[],
  x: number,
  z: number,
  yaw: number
): boolean {
  const closest = findClosestPoint(path, x, z);
  const tp = path[closest.index];
  const facingX = Math.cos(yaw);
  const facingZ = Math.sin(yaw);
  const dot = facingX * tp.dx + facingZ * tp.dz;
  return dot < -0.3;
}
